import React, { useMemo } from 'react';
import { Box, Typography, List, ListItem, Paper, Chip } from '@mui/material';
import { calendarBooking } from '../../types/calendarBooking';
import { groupEventsByDate, isToday, getFormattedDate, isAllDayEvent, formatEventTime, parseDateTime } from '../../services/calendarUtils';
import { startOfDay } from 'date-fns';

interface AgendaViewProps {
  currentDate: Date;
  events: calendarBooking[];
  onDateChange: (date: Date) => void;
  onEventClick?: (booking: calendarBooking) => void;
}

const AGENDA_APPOINTMENT_LIMIT = 20; // Maximum number of upcoming appointments to display

const AgendaView: React.FC<AgendaViewProps> = ({
  currentDate,
  events,
  onDateChange,
  onEventClick
}) => {
  // Filter for upcoming appointments only and limit the count
  const upcomingEvents = useMemo(() => {
    const today = startOfDay(new Date());

    // Filter events that start today or in the future
    const filtered = events.filter(event => {
      if (!event?.start?.dateTime) return false;

      try {
        const eventDate = parseDateTime(event.start);
        return eventDate >= today;
      } catch (error) {
        return false;
      }
    });

    // Sort by start date
    const sorted = filtered.sort((a, b) => {
      const dateA = parseDateTime(a.start);
      const dateB = parseDateTime(b.start);
      return dateA.getTime() - dateB.getTime();
    });

    // Limit to first N appointments
    return sorted.slice(0, AGENDA_APPOINTMENT_LIMIT);
  }, [events]);

  const groupedEvents = groupEventsByDate(upcomingEvents);
  const sortedDates = Object.keys(groupedEvents).sort();

  return (
    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', py: 2 }}>
      {sortedDates.length > 0 ? (
        sortedDates.map((dateKey) => (
          <Box key={dateKey} sx={{ mb: 3 }}>
            <Box sx={{ p: 1, bgcolor: 'rgba(224, 222, 222, 0.12)', borderRadius: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: isToday(new Date(dateKey)) ? 'bold' : 'normal',
                  color: isToday(new Date(dateKey)) ? 'primary.main' : 'text.primary',
                }}
              >
                {getFormattedDate(dateKey)}
              </Typography>
            </Box>

            {/* Event List */}
            <List sx={{ mt: 1 }}>
              {groupedEvents[dateKey].map((event) => (
                <Paper
                  key={event.bookingId || Math.random().toString(36).substr(2, 9)}
                  elevation={1}
                  onClick={() => onEventClick && onEventClick(event)}
                  sx={{
                    mb: 1,
                    boxShadow: 0,
                    cursor: 'pointer', // Add this to show it's clickable
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                      {/* Event Time */}
                      <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 'bold', marginRight: 2, width: '150px' }}>
                        {isAllDayEvent(event) ? 'All day' : 
                        `${formatEventTime(event)}`}
                      </Typography>

                      <Box sx={{
                        width: 8,
                        height: 8,
                        borderRadius: 100,
                        backgroundColor: event?.color,
                      }}></Box>

                      <Typography variant="body1" sx={{ fontWeight: 'medium', mx: 2, flexGrow: 1 }}>
                        {event?.customerName}
                      </Typography>
                      
                      {event?.serviceName && (
                        <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
                          {event.serviceName}
                        </Typography>
                      )}
                    </Box>
                  </ListItem>
                </Paper>
              ))}
            </List>
          </Box>
        ))
      ) : (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No events scheduled
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AgendaView;